import fs from 'node:fs'
import path from 'node:path'
import os from 'node:os'

/**
 * Returns the stable gsd-munch home directory path.
 * Mirrors upstream GSD's ~/.claude/get-shit-done/ pattern.
 */
export function getGsdMunchHome(): string {
  return path.join(os.homedir(), '.claude', 'gsd-munch')
}

/**
 * Returns the package root directory (one level above dist/).
 */
export function getPkgRoot(): string {
  return path.resolve(__dirname, '..')
}

/**
 * Transforms file content by replacing all upstream GSD path references
 * with the gsd-munch equivalents. Three passes in order:
 *
 * 1. Replace bash-invoked gsd-tools path (shell expansion not available in @-refs)
 * 2. Replace any remaining get-shit-done path fragments (catches @-refs and absolute paths)
 * 3. Replace /gsd: command namespace with /gm:
 * 4. Replace name: gsd: frontmatter field with name: gm:
 *
 * CRITICAL: After transformation, output must not contain 'get-shit-done'.
 */
export function transformContent(content: string, gsdMunchHome: string): string {
  return content
    // Pass 1: Replace bash-invoked gsd-tools path (quoted shell invocation)
    .replaceAll(
      '"$HOME/.claude/get-shit-done/bin/gsd-tools.cjs"',
      `"${gsdMunchHome}/bin/gsd-tools.cjs"`
    )
    // Pass 2: Replace any remaining get-shit-done path fragments
    // Matches paths like @/Users/foo/.claude/get-shit-done/ or /Users/foo/.claude/get-shit-done/
    // Uses a capture group to preserve any leading @ character from @-references
    .replaceAll(
      /(@?)\/[^\s"']*\.claude\/get-shit-done\//g,
      `$1${gsdMunchHome}/`
    )
    // Pass 3: Replace name: gsd: frontmatter field
    .replaceAll('name: gsd:', 'name: gm:')
    // Pass 4: Replace /gsd: command namespace references
    .replaceAll('/gsd:', '/gm:')
}

/**
 * Recursively copies files from srcDir to destDir, applying transformer to each file's content.
 * Creates destDir if it doesn't exist.
 */
export async function deployDirectory(
  srcDir: string,
  destDir: string,
  transformer: (content: string) => string
): Promise<void> {
  fs.mkdirSync(destDir, { recursive: true })
  const entries = fs.readdirSync(srcDir, { withFileTypes: true })
  for (const entry of entries) {
    const srcPath = path.join(srcDir, entry.name)
    const destPath = path.join(destDir, entry.name)
    if (entry.isDirectory()) {
      await deployDirectory(srcPath, destPath, transformer)
    } else {
      const content = fs.readFileSync(srcPath, 'utf-8')
      fs.writeFileSync(destPath, transformer(content), 'utf-8')
    }
  }
}

/**
 * Deploys all GSD workflow assets to the gsd-munch home directory.
 * Deploys: workflows/, templates/, references/, bin/ directories + VERSION file.
 * All file content is transformed with path substitution before writing.
 *
 * @param gsdMunchHome Deployment target directory (defaults to ~/.claude/gsd-munch)
 */
export async function deployAssets(gsdMunchHome = getGsdMunchHome()): Promise<void> {
  const pkgRoot = getPkgRoot()
  const transformer = (content: string) => transformContent(content, gsdMunchHome)

  const dirs = ['workflows', 'templates', 'references', 'bin', 'hooks', 'contexts']
  for (const dir of dirs) {
    const srcDir = path.join(pkgRoot, dir)
    if (fs.existsSync(srcDir)) {
      await deployDirectory(srcDir, path.join(gsdMunchHome, dir), transformer)
    }
  }

  // Copy VERSION verbatim (no transformation needed)
  const versionSrc = path.join(pkgRoot, 'VERSION')
  if (fs.existsSync(versionSrc)) {
    fs.mkdirSync(gsdMunchHome, { recursive: true })
    fs.copyFileSync(versionSrc, path.join(gsdMunchHome, 'VERSION'))
  }
}
